// src/pages/Dashboard/sections/network-services/commissionApiService.js
import { getValidSignature, clearSignatureCache } from '../../../../services/signatureService';

/**
 * Cache for signature timestamps to prevent too frequent refreshes
 * @type {Object}
 */
const signatureTimestamps = {
  lastSuccess: 0,
  lastFailure: 0
};

/**
 * Cache for commission data to reduce repeated fetches
 * @type {Object}
 */
const commissionDataCache = {
  data: null,
  timestamp: 0,
  nodeId: null,
  period: null,
  page: null
};

/**
 * Check if cached commission data is still valid
 * @param {string|number} nodeId - Node ID
 * @param {string} period - Time period
 * @param {number} page - Page number
 * @returns {boolean} - True if cache is valid
 */
const isCacheValid = (nodeId, period, page) => {
  const now = Date.now();
  // Cache is valid for 60 seconds
  const cacheValidityPeriod = 60 * 1000;
  
  return (
    commissionDataCache.data &&
    commissionDataCache.nodeId === nodeId &&
    commissionDataCache.period === period &&
    commissionDataCache.page === page &&
    (now - commissionDataCache.timestamp) < cacheValidityPeriod
  );
};

/**
 * Fetch commission data for the selected node
 * OPTIMIZED: Added caching and reduced signature refreshes
 * 
 * @param {Object} wallet - Wallet information with connection status
 * @param {string} period - The period to fetch commissions for ('7d', '30d', '90d', 'all')
 * @param {number} page - Page number for pagination
 * @param {string|number} nodeId - Optional node ID to filter data for specific node
 * @returns {Object} - Processed commission data
 */
export const fetchCommissionData = async (wallet, period = '30d', page = 1, nodeId = null) => {
  if (!wallet.connected) {
    throw new Error('Wallet not connected');
  }
  
  console.log(`Fetching commission data for node ${nodeId}, period ${period}, page ${page}`);
  
  // Check if we have valid cached data
  if (isCacheValid(nodeId, period, page)) {
    console.log("Using cached commission data");
    return commissionDataCache.data;
  }
  
  // OPTIMIZATION: Add tracking for retry attempts to prevent infinite loops
  let retryCount = 0;
  const MAX_RETRIES = 1; // Only try once more after initial failure
  
  // Check if we need a new signature
  const now = Date.now();
  const needsNewSignature = (
    // No successful signature in the last 30 minutes
    (now - signatureTimestamps.lastSuccess) > 30 * 60 * 1000 ||
    // Last attempt failed and it's been at least 10 seconds
    ((now - signatureTimestamps.lastFailure) > 10 * 1000 && signatureTimestamps.lastFailure > signatureTimestamps.lastSuccess)
  );
  
  const fetchWithRetry = async (forceRefresh = false) => {
    try {
      // Get a valid signature with potential caching
      const { signature, message } = await getValidSignature(
        wallet.address,
        null, // No blockchain network ID needed
        wallet.type || 'metamask',
        forceRefresh // Only force refresh if explicitly requested
      );
      
      // If signature obtained successfully, update the timestamp
      signatureTimestamps.lastSuccess = Date.now();
      
      // Build request body
      const requestBody = {
        wallet_address: wallet.address,
        signature: signature,
        message: message,
        wallet_type: wallet.type || 'metamask',
        period: period,
        page: page,
        page_size: 20,
        include_details: true
      };
      
      // Add node_id if provided
      if (nodeId) {
        requestBody.node_id = nodeId;
      }
      
      console.log("Fetching commission data with request body:", {
        ...requestBody,
        signature: signature ? signature.substring(0, 10) + '...' : null // Log only part of the signature for security
      });
      
      // Fetch commission data - FIXED URL PATH
      const response = await fetch('https://api.aeronyx.network/api/commissions/direct-data/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        // Check if it's an authentication error
        if (result.message && (
          result.message.toLowerCase().includes('signature') || 
          result.message.toLowerCase().includes('auth') ||
          result.message.toLowerCase().includes('token'))
        ) {
          // Record the failure time
          signatureTimestamps.lastFailure = Date.now();
          
          // Clear signature cache only when necessary
          clearSignatureCache();
          throw new Error('Signature validation failed, retrying...');
        }
        throw new Error(result.message || 'Failed to fetch commission data');
      }
      
      console.log("Commission data fetched successfully");
      
      // Process and cache the commission data
      const processedData = processCommissionData(result.data);
      
      // Update cache
      commissionDataCache.data = processedData;
      commissionDataCache.timestamp = Date.now();
      commissionDataCache.nodeId = nodeId;
      commissionDataCache.period = period;
      commissionDataCache.page = page;
      
      return processedData;
      
    } catch (error) {
      // Only retry for signature issues, and only if we haven't exceeded retry limit
      if (error.message && 
          error.message.includes('retrying') && 
          retryCount < MAX_RETRIES) {
        
        retryCount++;
        console.log(`Retry attempt ${retryCount} for commission data`);
        
        // Add a small delay before retrying to avoid rate limits
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return fetchWithRetry(true); // Force signature refresh on retry
      }
      
      // For other errors or if we've exhausted retries, rethrow
      throw error;
    }
  };
  
  // Start the fetch with potential retries
  return fetchWithRetry(needsNewSignature);
};

/**
 * Process and transform commission data from API for UI consumption
 * @param {Object} data - Raw commission data from API
 * @returns {Object} - Transformed commission data
 */
function processCommissionData(data) {
  // If data is empty or invalid, return null
  if (!data) return null;
  
  // Process pagination data if available
  let paginationInfo = null;
  if (data.details && data.details.pagination) {
    const pagination = data.details.pagination;
    paginationInfo = {
      current_page: pagination.current_page || 1,
      total_pages: pagination.total_pages || 1,
      total_records: pagination.total_records || 0,
      has_next: pagination.has_next || false,
      has_previous: pagination.has_previous || false,
      page_size: pagination.page_size || 20
    };
  }
  
  // Ensure commissions array exists in details
  if (data.details) {
    // Make sure details.commissions is an array
    data.details.commissions = data.details.commissions || [];
    
    // Add pagination info
    data.details.pagination = paginationInfo;
  }
  
  // Process node data if available
  if (data.nodes) {
    // Transform node commissions data for better consumption
    data.nodes = data.nodes.map(node => {
      return {
        ...node,
        commissions: {
          total: node.commissions?.total || 0,
          referrer_commissions: node.commissions?.referrer_commissions || 0,
          node_commissions: node.commissions?.node_commissions || 0,
          differential_commissions: node.commissions?.differential_commissions || 0,
          count: node.commissions?.count || 0
        }
      };
    });
  }
  
  // Process commission sources if available
  if (data.commission_sources) {
    // Ensure direct_nodes and second_level_nodes are arrays
    data.commission_sources.direct_nodes = data.commission_sources.direct_nodes || [];
    data.commission_sources.second_level_nodes = data.commission_sources.second_level_nodes || [];
    
    // Process direct nodes data
    data.commission_sources.direct_nodes = data.commission_sources.direct_nodes.map(node => {
      return {
        ...node,
        commissions_generated: {
          total: node.commissions_generated?.total || 0,
          count: node.commissions_generated?.count || 0,
          node_commissions: node.commissions_generated?.node_commissions || 0,
          differential_commissions: node.commissions_generated?.differential_commissions || 0
        }
      };
    });
    
    // Process second level nodes data
    data.commission_sources.second_level_nodes = data.commission_sources.second_level_nodes.map(node => {
      return {
        ...node,
        commissions_generated: {
          total: node.commissions_generated?.total || 0,
          count: node.commissions_generated?.count || 0,
          differential_commissions: node.commissions_generated?.differential_commissions || 0
        }
      };
    });
  }
  
  return data;
}
